// Session storage key for internal navigation context
export const NAVIGATION_CONTEXT_KEY = 'velo_navigation_context';
// Session storage key for rich navigation context from dashboard-ui
export const SOURCE_NAVIGATION_CONTEXT_KEY = 'velo_source_context';

/**
 * Get navigation context from session storage
 * @returns {object|null} Navigation context or null if not found
 */
export const getStoredNavigationContext = () => {
  try {
    const stored = sessionStorage.getItem(NAVIGATION_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('[SecureStorage] Failed to parse navigation context from session storage:', error);
    return null;
  }
};

/**
 * Get rich source navigation context from dashboard-ui (without cleanup)
 * @returns {object|null} Source navigation context or null if not found
 */
export const getSourceNavigationContext = () => {
  try {
    const stored = sessionStorage.getItem(SOURCE_NAVIGATION_CONTEXT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn(
      '[SecureStorage] Failed to parse source navigation context from session storage:',
      error
    );
    return null;
  }
};

/**
 * Clear source navigation context from session storage
 * @returns {boolean} Success status
 */
export const clearSourceNavigationContext = () => {
  try {
    sessionStorage.removeItem(SOURCE_NAVIGATION_CONTEXT_KEY);
    return true;
  } catch (error) {
    console.warn('[SecureStorage] Failed to clear source navigation context:', error);
    return false;
  }
};

/**
 * Store navigation context in session storage
 * @param {object} context - Navigation context to store
 * @returns {boolean} Success status
 */
export const storeNavigationContext = (context) => {
  try {
    sessionStorage.setItem(NAVIGATION_CONTEXT_KEY, JSON.stringify(context));
    return true;
  } catch (error) {
    console.warn('[SecureStorage] Failed to store navigation context:', error);
    // Silently fail if sessionStorage is not available
    return false;
  }
};

/**
 * Clear navigation context from session storage
 * @returns {boolean} Success status
 */
export const clearNavigationContext = () => {
  try {
    sessionStorage.removeItem(NAVIGATION_CONTEXT_KEY);
    return true;
  } catch (error) {
    console.warn('[SecureStorage] Failed to clear navigation context:', error);
    return false;
  }
};

/**
 * Create a new navigation context object
 * @param {object} location - Current location object
 * @returns {object} Navigation context
 */
export const createNavigationContext = (location) => {
  // First, check for rich source context from dashboard-ui
  const sourceContext = getSourceNavigationContext();

  // Check if there's already a stored context to preserve original entry point
  let existingContext = null;
  try {
    const stored = sessionStorage.getItem(NAVIGATION_CONTEXT_KEY);
    existingContext = stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('[SecureStorage] Could not retrieve existing context for preservation:', error);
  }

  const currentPath = location.pathname;
  const currentReferrer = document.referrer;

  // Use referrer from source context if available, otherwise fall back to document.referrer
  const referrer = sourceContext?.referrer || currentReferrer;

  const context = {
    referrer,
    historyLength: window.history.length,
    entryTime: Date.now(),
    // Include the rich source context if available
    sourceContext: sourceContext || null
  };

  // If we have an existing context and this looks like a redirect within the app,
  // preserve the original referrer
  // BUT only if we don't have a fresh sourceContext (which takes precedence)
  if (
    !sourceContext &&
    existingContext &&
    existingContext.referrer &&
    existingContext.entryTime &&
    Date.now() - existingContext.entryTime < 60000
  ) {
    // Extended to 60 seconds for longer chains

    // Only preserve if we're navigating within the guide flow or to excluded routes
    const isGuideFlow = currentPath.startsWith('/guide');
    const currentReferrerInternal =
      currentReferrer && currentReferrer.includes(window.location.origin);
    const currentReferrerIsGuide = currentReferrerInternal && currentReferrer.includes('/guide');

    // Preserve original context if:
    // 1. We're in the guide flow AND the current referrer is also in the guide flow
    // 2. OR we're moving to an excluded route
    if (
      (isGuideFlow && currentReferrerIsGuide) ||
      currentPath.includes('guide/thread') ||
      currentPath.includes('post-purchase')
    ) {
      context.referrer = existingContext.referrer;
      context.originalHistoryLength = existingContext.historyLength;
    }
  }

  return context;
};
